import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users, Sparkles, ArrowRight, Lock, Linkedin } from "lucide-react";
import Orbs from "../components/Orbs";
import { Section, Overline } from "../components/Layout";
import { api } from "../lib/api";
import { fmtTimePT, fmtDatePT, fmtWeekdayPT, TZ } from "../lib/time";
import { SlotTile, SharedCapitalBookingForm } from "./CapitalSeriesPage";
import VerifyGate from "../components/VerifyGate";
import RegistrationForm from "../components/RegistrationForm";
import { needsVerification } from "../lib/register";
import RichText from "../components/RichText";
import { resolveImageUrl } from "../lib/media";
import { sessionTitle } from "../lib/sessionTitle";
import { captureUtmFromLocation, getUtmForRegistration } from "../lib/utm";

/**
 * Standalone view for a shared event/series link (/e/:token).
 * Renders a single event or Founder Labs page with NO nav and no way to browse elsewhere.
 */
export default function StandaloneEvent() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    captureUtmFromLocation();
    api.get(`/share/${token}`).then((r) => setData(r.data)).catch(() => setErr("This link is invalid or has expired."));
  }, [token]);

  if (err) return <ErrShell msg={err} />;
  if (!data) return <div className="relative min-h-screen"><Orbs /><Section className="pt-24"><div className="skeleton h-64 max-w-3xl mx-auto" /></Section></div>;

  return (
    <div className="relative min-h-screen" data-testid="standalone-event-page">
      <Orbs />
      <div className="relative z-10">
        {/* Chrome-less header: brand only, no nav */}
        <header className="pt-6 pb-3">
          <div className="max-w-[1400px] mx-auto px-6 flex items-center gap-2.5">
            <img src="/venturedock-logo.webp" alt="Venture Dock" className="h-7 w-auto" />
            <span className="ml-auto text-[10px] font-mono uppercase tracking-widest text-muted-foreground flex items-center gap-1"><Lock size={10} />Private link</span>
          </div>
        </header>
        {data.kind === "event" ? <EventStandalone e={data.event} token={token} /> : <SeriesStandalone s={data.series} token={token} />}
      </div>
    </div>
  );
}

function EventStandalone({ e }) {
  const [reg, setReg] = useState({ name: "", email: "", note: "" });
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(false);
  const [verified, setVerified] = useState(null);

  // eslint-disable-next-line no-unused-vars
  const submit = async () => {
    if (!reg.name || !reg.email) return toast.error("Name and email required");
    setBusy(true);
    try {
      const r = await api.post(`/events/${e.id}/register`, {
        name: reg.name,
        email: verified?.email || reg.email,
        guests: [],
        answers: reg.note ? { note: reg.note } : {},
        registration_token: verified?.token,
        ...getUtmForRegistration(),
      });
      setStatus(r.data.status === "pending" ? "pending" : r.data.status === "waitlist" ? "waitlist" : "done");
      toast.success(r.data.status === "pending" ? "Registration received — under review" : "You're registered");
    } catch (er) { toast.error(er.response?.data?.detail || "Registration failed"); }
    finally { setBusy(false); }
  };

  return (
    <Section className="pt-4 pb-16" innerClassName="max-w-3xl mx-auto">
      <div className="card-hair overflow-hidden">
        <div className="aspect-[16/6] overflow-hidden">
          {e.cover_image && <img src={resolveImageUrl(e.cover_image)} alt={e.title} className="w-full h-full object-cover" />}
        </div>
        <div className="p-8">
          <div className="flex flex-wrap gap-1.5 mb-3">
            <span className={`chip ${e.visibility === "public" ? "chip-peach" : "chip-purple"}`}>{e.visibility === "public" ? "Public" : "Members-only"}</span>
          </div>
          <h1 className="font-heading text-4xl tracking-tight">{e.title}</h1>
          <div className="mt-3 flex flex-wrap gap-4 text-xs text-muted-foreground font-mono uppercase">
            <span className="flex items-center gap-1"><CalendarDays size={12} />{fmtDatePT(e.start_at, { weekday: "short", month: "short", day: "numeric" })} · {fmtTimePT(e.start_at)} PT</span>
            <span className="flex items-center gap-1"><MapPin size={12} />{e.campus_id === "cmp_pa" ? "Palo Alto" : e.campus_id === "cmp_mp" ? "Milpitas" : e.custom_venue || "Virtual"}</span>
          </div>
          {e.description && <RichText html={e.description} className="mt-5 text-sm text-muted-foreground leading-relaxed" testId="standalone-event-description" />}

          {status ? (
            <div className="glass-panel p-6 mt-8 text-center" data-testid="standalone-confirmation">
              <div className="h-12 w-12 rounded-full mx-auto flex items-center justify-center" style={{ background: "hsl(var(--vd-green) / 0.14)", color: "hsl(var(--vd-green))" }}><Sparkles size={18} /></div>
              <div className="mt-3 font-heading text-xl">{status === "pending" ? "Under review" : status === "waitlist" ? "You're on the waitlist" : "You're registered"}</div>
              <div className="text-sm text-muted-foreground mt-1">{status === "pending" ? "The organizer reviews every registration — we'll email you a decision." : `A confirmation is on its way to ${verified?.email || reg.email}.`}</div>
            </div>
          ) : (needsVerification(e) && !verified) ? (
            <div className="glass-panel p-5 mt-8" data-testid="standalone-verify">
              <VerifyGate targetId={e.id} onCancel={() => {}} onVerified={(v) => { setVerified(v); setReg((r) => ({ ...r, email: v.email, name: v.prefill?.name || r.name })); }} />
            </div>
          ) : (
            <div className="glass-panel p-5 mt-8" data-testid="standalone-register">
              <RegistrationForm
                ev={e}
                verified={verified}
                ctaLabel="Reserve my spot"
                onCancel={() => {}}
                onDone={(state) => setStatus(state.status === "pending" ? "pending" : state.status === "waitlist" ? "waitlist" : "done")}
              />
            </div>
          )}
        </div>
      </div>
    </Section>
  );
}

function SeriesStandalone({ s: initialSeries }) {
  const series = initialSeries;
  const [slots, setSlots] = useState([]);
  const [selected, setSelected] = useState(null);
  const [confirmation, setConfirmation] = useState(null);
  const [verified, setVerified] = useState(null);

  useEffect(() => {
    api.get(`/sessions/series/${series.id}/slots`).then((r) => setSlots(r.data)).catch(() => {});
  }, [series.id]);

  const reload = () => api.get(`/sessions/series/${series.id}/slots`).then((r) => setSlots(r.data));

  const byWeek = useMemo(() => {
    const map = new Map();
    slots.forEach((sl) => {
      const day = fmtDatePT(sl.start_at, { year: "numeric", month: "2-digit", day: "2-digit" });
      if (!map.has(day)) map.set(day, []);
      map.get(day).push(sl);
    });
    return [...map.entries()].map(([k, v]) => ({ key: k, slots: v.sort((a, b) => a.start_at.localeCompare(b.start_at)) }));
  }, [slots]);

  return (
    <Section className="pt-4 pb-16" innerClassName="max-w-4xl mx-auto">
      <div className="card-hair overflow-hidden p-8" data-testid="standalone-series-header">
        <div className="flex items-center gap-4">
          <div className="h-20 w-20 rounded-2xl overflow-hidden border border-white/60 bg-neutral-100 shrink-0">
            <img src={resolveImageUrl(series.investor_photo)} alt={series.investor_name} className="w-full h-full object-cover object-top" />
          </div>
          <div>
            <span className="chip chip-purple text-[10px]">Founder Labs</span>
            <h1 className="font-heading text-3xl tracking-tight mt-1 line-clamp-2" title={sessionTitle(series)} data-testid="standalone-series-title">{sessionTitle(series)}</h1>
            <div className="text-sm text-muted-foreground mt-1">{series.investor_name}{series.investor_firm ? ` · ${series.investor_firm}` : ""}</div>
          </div>
        </div>
        <RichText html={series.investor_bio} className="mt-6 text-sm text-muted-foreground leading-relaxed" testId="standalone-series-bio" />
        {series.investor_linkedin && (
          <a href={series.investor_linkedin.startsWith("http") ? series.investor_linkedin : `https://${series.investor_linkedin}`} target="_blank" rel="noreferrer" className="mt-3 text-xs font-mono hover:underline inline-flex items-center gap-1.5" style={{ color: "hsl(var(--vd-green))" }} data-testid="standalone-investor-linkedin">
            <Linkedin size={12} strokeWidth={2.25} />
            <span className="lowercase">linkedin</span>
            <span aria-hidden="true">↗</span>
          </a>
        )}
        <div className="mt-4 flex flex-wrap gap-1.5">{(series.focus_areas || []).map((f) => <span key={f} className="chip text-[10px]">{f}</span>)}</div>
        <div className="mt-6 flex items-center gap-2 text-xs text-muted-foreground font-mono flex-wrap">
          <MapPin size={12} />{series.campus_id === "cmp_pa" ? "Palo Alto" : "Milpitas"}
        </div>
      </div>

      <div className="mt-6 space-y-5" data-testid="standalone-series-slots">
        {byWeek.length === 0 && <div className="glass-panel p-8 text-center text-sm text-muted-foreground">No upcoming sessions yet.</div>}
        {byWeek.map((wk) => {
          const openCount = wk.slots.filter((sl) => sl.status === "available" && !sl.is_past).length;
          const first = wk.slots[0];
          const activeSelected = selected && wk.slots.some((sl) => sl.id === selected.id);
          return (
            <div key={wk.key} className="glass-panel p-6" data-testid={`standalone-week-${wk.key}`}>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="overline">{fmtWeekdayPT(first.start_at)}</div>
                  <div className="font-heading text-xl mt-0.5">{new Date(first.start_at).toLocaleDateString("en-US", { timeZone: TZ, month: "long", day: "numeric" })}</div>
                </div>
                <div className="font-mono text-xs text-muted-foreground">
                  <span style={{ color: openCount > 0 ? "hsl(var(--vd-green))" : "hsl(var(--muted-foreground))" }}>{openCount}</span> / {wk.slots.length} open
                </div>
              </div>
              <div className="grid grid-cols-4 gap-2.5">
                {wk.slots.map((sl) => <SlotTile key={sl.id} s={sl} selected={selected} setSelected={setSelected} />)}
              </div>
              {activeSelected && !confirmation && (
                (needsVerification(series) && !verified) ? (
                  <div className="mt-5 pt-4"><div className="glass-panel p-5">
                    <VerifyGate targetId={series.id} onCancel={() => setSelected(null)} onVerified={setVerified} />
                  </div></div>
                ) : (
                  <SharedCapitalBookingForm series={series} slot={selected} member={null} user={null} verified={verified} onCancel={() => setSelected(null)} onDone={(state) => { setConfirmation(state); reload(); }} />
                )
              )}
              {activeSelected && confirmation && (
                <div className="mt-5 pt-4" data-testid="standalone-capital-confirmation">
                  <div className="glass-panel p-5 text-center">
                    <div className="h-14 w-14 rounded-full mx-auto flex items-center justify-center" style={{ background: "hsl(var(--vd-peach) / 0.14)", color: "hsl(var(--vd-peach-ink))" }}><Sparkles size={22} /></div>
                    <div className="mt-4 font-heading text-xl">Request sent</div>
                    <div className="text-sm text-muted-foreground mt-1">Admin confirms within 24 hours.</div>
                    <button onClick={() => { setConfirmation(null); setSelected(null); }} className="btn-ghost text-sm mt-4">Close</button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function ErrShell({ msg }) {
  return (
    <div className="relative min-h-screen"><Orbs /><Section className="pt-24" innerClassName="max-w-md mx-auto text-center">
      <div className="glass-panel p-10">
        <div className="h-12 w-12 mx-auto rounded-full flex items-center justify-center" style={{ background: "hsl(var(--vd-coral) / 0.14)", color: "hsl(var(--destructive))" }}><Lock size={18} /></div>
        <div className="font-heading text-xl mt-4">{msg}</div>
        <div className="text-xs text-muted-foreground mt-2">Ask the sender to share a fresh link.</div>
      </div>
    </Section></div>
  );
}
